import React, { useEffect, useState } from "react";

// Import bootstrap components
import { Row, Col, Card, Form, Button, Table, Spinner } from "react-bootstrap";

// Import toast utilities
import { showSuccessToast } from "utils/toast";

// Import formik & Yup
import { Formik } from "formik";
import * as Yup from "yup";

// Import redux components
import { useDispatch, useSelector } from "react-redux";

// Import role slice
import {
  addRole as addRoleToStore,
  updateRole as updateRoleInStore,
  deleteRole as deleteRoleFromStore,
} from "state/slices/role/roleSlice";

// Import role mutation
import {
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "state/slices/role/roleApiSlice";

// Import aut slice
import { updateUserInfo } from "state/slices/auth/authSlice";

// Import Utilities
import { isUserOwnTheData } from "utils/authentication";
import {
  clientPermissions,
  convertServerRoleToClient,
  convertClientPermissionsToServer,
} from "utils/roles";

// Importing Constants
import { SUPPORTED_DASHBOARDS } from "constants";

const initialRowValues = {
  id: null,
  name: "",
  dashboardId: SUPPORTED_DASHBOARDS.EMPLOYEE,
  isDefault: false,
  isTeamWise: true,
  isAdmin: false,
  ...clientPermissions,
};

const GeneralAccessManagement = ({
  isAllowedToRead,
  isAllowedToCreate,
  isAllowedToUpdate,
  isAllowedToDelete,
  RoleViewManagement,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [formikValues, setFormikValues] = useState(initialRowValues);

  const dispatch = useDispatch();

  const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  // Refetch users
  // const { refetch: userRefetch } = useGetUsersQuery();

  const addRole = async (roleData) => {
    try {
      const data = await createRole(roleData).unwrap();

      dispatch(addRoleToStore(data));

      showSuccessToast("Role added successfully");
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  const updateRoleManage = async (roleId, roleData) => {
    try {
      const data = await updateRole({
        id: roleId,
        data: roleData,
      }).unwrap();

      const updatedRole = {
        ...data.role,
      };

      dispatch(updateRoleInStore(updatedRole));

      // Update team of current user
      if (isUserOwnTheData(loggedUserInfo, "role", data?.role.id)) {
        dispatch(
          updateUserInfo({
            field: "role",
            data: data.role,
          })
        );
      }

      showSuccessToast("Role updated successfully");

      // userRefetch();
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  const deleteRoleManage = async () => {
    try {
      await deleteRole(selectedRow.id).unwrap();

      dispatch(deleteRoleFromStore(selectedRow.id));

      showSuccessToast("Role deleted successfully");

      // userRefetch();

      if (selectedRow.id) {
        setFormikValues(initialRowValues);
        setSelectedRow(null);
      }
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  const handleCheckboxChange = (event, row) => {
    if (event.target.checked) {
      setSelectedRow(row);
    } else {
      setSelectedRow(null);
      setFormikValues(initialRowValues);
    }
  };

  const handleEdit = (row) => {
    const data = convertServerRoleToClient(row);

    setFormikValues(data);
  };

  const handleSubmit = (values, { resetForm }) => {
    let {
      isDefault,
      id,
      name,
      dashboardId,
      isTeamWise,
      isAdmin,
      ...permissions
    } = values;

    isDefault = (isDefault === "true" || isDefault) === true ? true : false;

    const includedAllowedRoutes = convertClientPermissionsToServer(permissions);

    const updatedValues = {
      name,
      dashboardId,
      isDefault,
      isTeamWise,
      isAdmin,
    };

    if (includedAllowedRoutes.length) {
      updatedValues.allowedRoutes = includedAllowedRoutes;
    }

    if (id) {
      updateRoleManage(id, updatedValues);

      // Rest Form Data
      setFormikValues(initialRowValues);
    } else {
      addRole(updatedValues);
    }

    if (selectedRow?.id) {
      setSelectedRow(null);
    }

    resetForm();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Role name is required"),
    isDefault: Yup.string().required("Default Role is required"),
    isTeamWise: Yup.string().required("Team Wise is required"),
    dashboardId: Yup.string().required("Please select the dashboard"),
  });

  return (
    <Row>
      {isAllowedToCreate && (
        <Col md={12}>
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  handleChange,
                  handleSubmit,
                  touched,
                  errors,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                    <h5 className="mb-3">Role Details</h5>
                    <Row>
                      <Col md={4} className="mb-3">
                        <Form.Group className="form-group required" controlId="name">
                          <Form.Label className="mb-3 control-label">Role Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            placeholder="Enter role name"
                            value={values.name}
                            onChange={handleChange}
                            isInvalid={touched.name && !!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="mb-3">
                        <Form.Group className="form-group required" controlId="isDefault">
                          <Form.Label className="mb-3 control-label">Is Default?</Form.Label>
                          <Form.Control
                            as="select"
                            name="isDefault"
                            value={values.isDefault}
                            onChange={handleChange}
                            isInvalid={touched.isDefault && !!errors.isDefault}
                          >
                            <option value="">Default role</option>
                            <option value={false}>No</option>
                            <option value={true}>Yes</option>
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.isDefault}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="mb-3">
                        <Form.Group className="form-group required" controlId="isTeamWise">
                          <Form.Label className="mb-3 control-label">Is Team Wise?</Form.Label>
                          <Form.Control
                            as="select"
                            name="isTeamWise"
                            value={values.isTeamWise}
                            onChange={handleChange}
                            isInvalid={
                              touched.isTeamWise && !!errors.isTeamWise
                            }
                          >
                            <option value="">Team Wise</option>
                            <option value={false}>No</option>
                            <option value={true}>Yes</option>
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.isTeamWise}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={2} className="mb-3">
                        <Form.Group className="form-group required" controlId="isAdmin">
                          <Form.Label className="mb-3 control-label">Is Admin?</Form.Label>
                          <Form.Control
                            as="select"
                            name="isAdmin"
                            value={values.isAdmin}
                            onChange={handleChange}
                            isInvalid={touched.isAdmin && !!errors.isAdmin}
                          >
                            <option value="">Is Admin</option>
                            <option value={false}>No</option>
                            <option value={true}>Yes</option>
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.isAdmin}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="mb-3">
                        <Form.Group className="form-group required" controlId="dashboardId">
                          <Form.Label className="mb-3 control-label">Assign Dashboard</Form.Label>
                          <Form.Control
                            as="select"
                            name="dashboardId"
                            value={values.dashboardId}
                            onChange={handleChange}
                            isInvalid={
                              touched.dashboardId && !!errors.dashboardId
                            }
                          >
                            <option value="">Dashboard</option>
                            <option value={SUPPORTED_DASHBOARDS.ADMIN}>
                              Admin
                            </option>
                            <option value={SUPPORTED_DASHBOARDS.EMPLOYEE}>
                              Employee
                            </option>
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.dashboardId}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h5 className="mb-3">Permissions</h5>
                    <Table striped responsive bordered>
                      <thead>
                        <tr>
                          <th></th>
                          <th>Create</th>
                          <th>Read</th>
                          <th>Update</th>
                          <th>Delete</th>
                          <th>Is Display</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th>Employee Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="employee_mnj_create"
                              checked={values.employee_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="employee_mnj_read"
                              checked={values.employee_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="employee_mnj_update"
                              checked={values.employee_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="employee_mnj_delete"
                              checked={values.employee_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="employee_mnj_display"
                              checked={values.employee_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Profile Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="profile_mnj_create"
                              checked={values.profile_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="profile_mnj_read"
                              checked={values.profile_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="profile_mnj_update"
                              checked={values.profile_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="profile_mnj_delete"
                              checked={values.profile_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="profile_mnj_display"
                              checked={values.profile_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Designation Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="designation_mnj_create"
                              checked={values.designation_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="designation_mnj_read"
                              checked={values.designation_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="designation_mnj_update"
                              checked={values.designation_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="designation_mnj_delete"
                              checked={values.designation_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="designation_mnj_display"
                              checked={values.designation_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Team Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="team_mnj_create"
                              checked={values.team_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="team_mnj_read"
                              checked={values.team_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="team_mnj_update"
                              checked={values.team_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="team_mnj_delete"
                              checked={values.team_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="team_mnj_display"
                              checked={values.team_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Shift Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="shift_mnj_create"
                              checked={values.shift_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="shift_mnj_read"
                              checked={values.shift_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="shift_mnj_update"
                              checked={values.shift_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="shift_mnj_delete"
                              checked={values.shift_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="shift_mnj_display"
                              checked={values.shift_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Role Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="role_mnj_create"
                              checked={values.role_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="role_mnj_read"
                              checked={values.role_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="role_mnj_update"
                              checked={values.role_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="role_mnj_delete"
                              checked={values.role_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="role_mnj_display"
                              checked={values.role_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Wfh Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="wfh_mnj_create"
                              checked={values.wfh_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="wfh_mnj_read"
                              checked={values.wfh_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="wfh_mnj_update"
                              checked={values.wfh_mnj_update}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="wfh_mnj_delete"
                              checked={values.wfh_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="wfh_mnj_display"
                              checked={values.wfh_mnj_display}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Holiday Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="holiday_mnj_create"
                              checked={values.holiday_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="holiday_mnj_read"
                              checked={values.holiday_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="holiday_mnj_update"
                              checked={values.holiday_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="holiday_mnj_delete"
                              checked={values.holiday_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="holiday_mnj_display"
                              checked={values.holiday_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Attendance Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="attendance_mnj_create"
                              checked={values.attendance_mnj_create}
                              onChange={handleChange}
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="attendance_mnj_read"
                              checked={values.attendance_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="attendance_mnj_update"
                              checked={values.attendance_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="attendance_mnj_delete"
                              checked={values.attendance_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="attendance_mnj_display"
                              checked={values.attendance_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Analytics Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="analytics_mnj_create"
                              checked={values.analytics_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="analytics_mnj_read"
                              checked={values.analytics_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="analytics_mnj_update"
                              checked={values.analytics_mnj_update}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="analytics_mnj_delete"
                              checked={values.analytics_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="analytics_mnj_display"
                              checked={values.analytics_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Settings Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="setting_mnj_create"
                              checked={values.setting_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="setting_mnj_read"
                              checked={values.setting_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="setting_mnj_update"
                              checked={values.setting_mnj_update}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="setting_mnj_delete"
                              checked={values.setting_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="setting_mnj_display"
                              checked={values.setting_mnj_display}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Search Management</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="search_mnj_create"
                              checked={values.search_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="search_mnj_read"
                              checked={values.search_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="search_mnj_update"
                              checked={values.search_mnj_update}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="search_mnj_delete"
                              checked={values.search_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="search_mnj_display"
                              checked={values.search_mnj_display}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Payroll Note</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="payrollnote_mnj_create"
                              checked={values.payrollnote_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="payrollnote_mnj_read"
                              checked={values.payrollnote_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="payrollnote_mnj_update"
                              checked={values.payrollnote_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="payrollnote_mnj_delete"
                              checked={values.payrollnote_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="payrollnote_mnj_display"
                              checked={values.payrollnote_mnj_display}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Mails</th>
                          <th>
                            <Form.Check
                              type="checkbox"
                              name="mail_mnj_create"
                              checked={values.mail_mnj_create}
                              onChange={handleChange}
                              disabled
                            />
                          </th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="mail_mnj_read"
                              checked={values.mail_mnj_read}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="mail_mnj_update"
                              checked={values.mail_mnj_update}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="mail_mnj_delete"
                              checked={values.mail_mnj_delete}
                              onChange={handleChange}
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="mail_mnj_display"
                              checked={values.mail_mnj_display}
                              onChange={(e) => {
                                handleChange(e);
                                if (values.isAdmin) {
                                  setFieldValue(
                                    "mail_mnj_create",
                                    e.target.checked
                                  );
                                  setFieldValue(
                                    "mail_mnj_read",
                                    e.target.checked
                                  );
                                  setFieldValue(
                                    "mail_mnj_update",
                                    e.target.checked
                                  );
                                  setFieldValue(
                                    "mail_mnj_delete",
                                    e.target.checked
                                  );
                                }
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Request Leave</th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="rleave_mnj_create"
                              checked={values.rleave_mnj_create}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="rleave_mnj_read"
                              checked={values.rleave_mnj_read}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="rleave_mnj_update"
                              checked={values.rleave_mnj_update}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="rleave_mnj_delete"
                              checked={values.rleave_mnj_delete}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              name="rleave_mnj_display"
                              checked={values.rleave_mnj_display}
                              onChange={(e) => {
                                handleChange(e);
                                if (values.isAdmin) {
                                  setFieldValue(
                                    "rleave_mnj_read",
                                    e.target.checked
                                  );
                                }
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <Row>
                      <Col>
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                                style={{ width: "18px", height: "18px" }}
                              />
                              {formikValues.id ? "Updating..." : "Adding..."}
                            </>
                          ) : (
                            formikValues.id ? "Save" : "Add"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      )}
      {isAllowedToRead && (
        <RoleViewManagement
          isAllowedToUpdate={isAllowedToUpdate}
          isAllowedToDelete={isAllowedToDelete}
          handleCheckboxChange={handleCheckboxChange}
          handleEdit={handleEdit}
          deleteRoleManage={deleteRoleManage}
          selectedRow={selectedRow}
        />
      )}
    </Row>
  );
};

export default GeneralAccessManagement;
